import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';

export interface ShortcutEntry {
  keys: string;
  description: string;
  group?: string;
}

interface ShortcutsContextValue {
  shortcuts: ShortcutEntry[];
  register: (id: string, entries: ShortcutEntry[]) => void;
  unregister: (id: string) => void;
  open: () => void;
  close: () => void;
}

const Ctx = createContext<ShortcutsContextValue | null>(null);

// Task #1665 — context globale per registrare keyboard shortcuts dinamici
// e mostrarli in un modal "Scorciatoie" attivabile da `?`. Ogni componente
// registra le proprie scorciatoie con un id stabile e le rimuove all'unmount.
export function ShortcutsHelpProvider({ children }: { children: React.ReactNode }) {
  const [registry, setRegistry] = useState<Record<string, ShortcutEntry[]>>({});
  const [isOpen, setIsOpen] = useState(false);

  const register = useCallback((id: string, entries: ShortcutEntry[]) => {
    setRegistry((prev) => ({ ...prev, [id]: entries }));
  }, []);
  const unregister = useCallback((id: string) => {
    setRegistry((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  }, []);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const shortcuts = useMemo(() => {
    const list: ShortcutEntry[] = [];
    Object.values(registry).forEach((arr) => list.push(...arr));
    return list;
  }, [registry]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      e.preventDefault();
      setIsOpen((v) => !v);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, ShortcutEntry[]>();
    shortcuts.forEach((s) => {
      const g = s.group || 'Generale';
      const arr = map.get(g) || [];
      arr.push(s);
      map.set(g, arr);
    });
    return Array.from(map.entries());
  }, [shortcuts]);

  return (
    <Ctx.Provider value={{ shortcuts, register, unregister, open, close }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Scorciatoie da tastiera</DialogTitle>
          </DialogHeader>
          {grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nessuna scorciatoia disponibile in questa schermata.</p>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {grouped.map(([group, entries]) => (
                <div key={group}>
                  <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">{group}</h4>
                  <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
                    {entries.map((s, i) => (
                      <li key={`${group}-${i}`} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span className="text-foreground/90">{s.description}</span>
                        <kbd className="px-2 py-0.5 rounded-md bg-muted text-xs font-mono border border-border">{s.keys}</kbd>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground text-center pt-2">
            Premi <kbd className="px-1 py-0.5 rounded bg-muted text-[10px] font-mono">?</kbd> per aprire o chiudere questa finestra.
          </p>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

export function useRegisterShortcuts(id: string, entries: ShortcutEntry[]): void {
  const ctx = useContext(Ctx);
  useEffect(() => {
    if (!ctx) return;
    ctx.register(id, entries);
    return () => ctx.unregister(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, [id]);
}

export function useShortcutsHelp(): ShortcutsContextValue | null {
  return useContext(Ctx);
}
